/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * Widget Subscriber Mobile Empty Component
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.13.0
 * @since 1.0.1
 */

import { FC } from 'react';

import { icons } from '../../../../../../assets';
import { LinkBlock } from '../../../../../../components/common';
import { FlexColumnStyled, FlexMiddleStyled } from '../../../../../../components/common/Block/Flexbox';
import { ImgStyled } from '../../../../../../components/common/Block/Img';
import { LayoutStyled } from '../../../../../../components/common/Block/Layout';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import IPageDashboardTranslation from '../../../../../../modules/internationalization/types/pages/dashboard/dashboard';
import { RoutePath } from '../../../../../../routes/constants/routesConstants';
import { WidgetHelpTextStyled, WidgetHelpTitleStyled } from '../../styled/WidgetHelpStyled';
import { WidgetEmptyCircleStyled } from '../../styled/WidgetStyled';
import { EWidgetSubscriberMobile } from '../widgetSubscriberMobileTypes';

export interface IWidgetSubscriberMobileEmpty {
    className?: string;
    isAllowToManageLabels?: boolean;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    translation: IPageDashboardTranslation;
    type: EWidgetSubscriberMobile;
}

const WidgetSubscriberMobileEmpty: FC<IWidgetSubscriberMobileEmpty> = props => {
    const { className, translation, type } = props;

    const onClickSettingsLabelsLink = (): void => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetSubscriberMobile.clickSetupLabelsLinkWidgetSubscriberMobile,
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    return (
        <FlexColumnStyled className={className}>
            <FlexMiddleStyled>
                <WidgetEmptyCircleStyled>
                    {{
                        labels:
                            <ImgStyled
                                className="medium"
                                src={icons.editIconMedium}/>,
                        tariffs:
                            <ImgStyled
                                className="medium"
                                src={icons.simCardMediumIcon}/>,
                    }[type]}

                </WidgetEmptyCircleStyled>
            </FlexMiddleStyled>
            <LayoutStyled className="mb-1 mt-3 t-center">
                <WidgetHelpTitleStyled>
                    {{
                        labels: translation.widgets.subscriberMobile.labels.empty.title,
                        tariffs: translation.widgets.subscriberMobile.tariffs.empty.title,
                    }[type]}
                </WidgetHelpTitleStyled>
            </LayoutStyled>
            <LayoutStyled className="mb-2 t-center">
                {{
                    labels:
                        <WidgetHelpTextStyled>
                            {translation.widgets.subscriberMobile.labels.empty.helpText}
                        </WidgetHelpTextStyled>,
                    tariffs:
                        <WidgetHelpTextStyled>
                            {translation.widgets.subscriberMobile.tariffs.empty.helpText}
                        </WidgetHelpTextStyled>,
                }[type]}
            </LayoutStyled>
            {{
                labels: <LayoutStyled className="mx-auto t-center">
                    {
                        props.isAllowToManageLabels &&
                        <LinkBlock dataName="redirectToSettingsLabels">
                            <LinkBlock
                                url={RoutePath.settingsLabels}
                                onClick={onClickSettingsLabelsLink}
                            >
                                {translation.widgets.subscriberMobile.labels.empty.btnText}
                            </LinkBlock>
                        </LinkBlock>
                    }
                </LayoutStyled>,
                tariffs:
                    <LayoutStyled className="mx-auto t-center">
                        <LinkBlock
                            dataName="redirectToSubscriberMobile"
                            url={RoutePath.subscriberMobile}>
                            {translation.widgets.subscriberMobile.tariffs.empty.btnText}
                        </LinkBlock>
                    </LayoutStyled>,
            }[type]}
        </FlexColumnStyled>
    );
};

export default WidgetSubscriberMobileEmpty;
